import {
  ConfigurationProvider as MuiAdminConfigurationProvider,
  NavigationContextProvider,
} from '@kirz/mui-admin';
import React, { useContext, useEffect } from 'react';
import {
  Routes as DomRoutes,
  Route,
  useNavigate,
  Navigate,
} from 'react-router-dom';

import { RouteGuard } from 'components/RouteGuard';
import { SplashLoader } from 'components/SplashLoader';
import { Configuration } from 'constants/configuration';
import { UserContext } from 'contexts/UserContext';
import { DefaultLayout } from 'layouts/DefaultLayout';
import { Categories } from 'pages/Categories';
import { Category } from 'pages/Category';
import { Clients } from 'pages/Clients';
import { FeaturedTemplates } from 'pages/FeaturedTemplates';
import { Filters } from 'pages/Filters';
import { Generations } from 'pages/Generations';
import { Login } from 'pages/Login';
import { Settings } from 'pages/Settings';
import { Tags } from 'pages/Tags';
import { Templates } from 'pages/Templates';

export default function App() {
  const { isUserLoading, user, hasPermission } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (isUserLoading || user) {
      return;
    }

    navigate('/login');
  }, [navigate, isUserLoading, user]);

  if (isUserLoading) {
    return <SplashLoader visible />;
  }

  const routes = [
    {
      path: 'templates',
      children: (
        <>
          <Route index element={<Templates />} />
        </>
      ),
    },
    // {
    //   path: 'lessons',
    //   children: (
    //     <>
    //       <Route index element={<Lessons />} />
    //       <Route path=":id" element={<Lesson />} />
    //     </>
    //   ),
    // },
    {
      path: 'categories',
      children: (
        <>
          <Route index element={<Categories />} />
          <Route path=":id" element={<Category />} />
        </>
      ),
    },
    {
      path: 'tags',
      element: <Tags />,
    },
    {
      path: 'featured-templates',
      element: <FeaturedTemplates />,
    },
    {
      path: 'filters',
      element: <Filters />,
    },
    {
      path: 'generations',
      element: <Generations />,
    },
    {
      path: 'settings',
      element: <Settings />,
    },
    {
      path: 'clients',
      element: <Clients />,
    },
    // {
    //   path: 'users',
    //   element: <Users />,
    // },
  ] as {
    path: string;
    permission?: string | string[];
    children?: React.ReactNode;
    element?: React.ReactNode;
  }[];

  const defaultRoute =
    routes.find(
      (x) =>
        !x.permission ||
        (typeof x.permission === 'string'
          ? hasPermission(x.permission)
          : !!x.permission.find((y) => hasPermission(y))),
    )?.path || '/';

  return (
    <MuiAdminConfigurationProvider {...Configuration.muiAdmin}>
      <NavigationContextProvider>
        <DomRoutes>
          <Route path="login" element={<Login />} />
          {user && (
            <>
              <Route path="/" element={<DefaultLayout />}>
                {routes.map((route) => {
                  const content = route.children || (
                    <Route index element={route.element} />
                  );

                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={<RouteGuard permission={route.permission} />}
                    >
                      {content}
                    </Route>
                  );
                })}
                <Route index element={<Navigate to={defaultRoute} />} />
              </Route>
              <Route path="*" element={<Navigate to={defaultRoute} />} />
            </>
          )}
        </DomRoutes>
        <SplashLoader visible={isUserLoading} />
      </NavigationContextProvider>
    </MuiAdminConfigurationProvider>
  );
}
